import React, { useEffect, useState } from 'react';
import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import {
  AllOrderCrumbSteps,
  errorMessage,
  Length,
  newOrderStatuses,
  OrderProcessingStepper,
  OrderProcessStatus,
  responseTexts,
  timers,
  productStatuses,
  serviceStatuses,
} from '@helpers/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { OrderProcessingBreadcrumbs } from '@components/OrderCreation/OrderProcessingBreadcrumbs';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import {
  AddonsListProps,
  Customer,
  OrderDetailsResponse,
} from '@helpers/Models';
import { PdfDownloadService } from '@services/PdfDownloadService';
import {
  formatDate,
  handleDateFormat,
  handleNewOrderInitiation,
} from '@services/OrderCreationService';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';

export const OrderDetails: React.FC = () => {
  const { state } = useLocation();
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState<string | null>(null);
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [finishedSteps, setFinishedSteps] = useState([
    'New Order',
    'Order Processing',
  ]);
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse>();
  const [addons, setAddons] = useState<AddonsListProps[]>([]);
  const [checkinOpen, setCheckinOpen] = useState<number | null>(null);
  const [serviceOpen, setServiceOpen] = useState<number | null>(null);
  const [productOpen, setProductOpen] = useState<number | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [totalAmountToCollected, setTotalAmountCollected] = useState<number>(0);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [resendNotification, setResendNotification] = useState<number>(0);
  // const [pickupBannerMessage, setPickupBannerMessage] = useState<string>('');

  /**
   * useEffect hook to handle order detail retrieval on component mount or when location state changes.
   *
   * - Initializes orderId from location state if available
   * - If orderId is valid, it triggers fetchOrderDetails() to load the corresponding order
   * - If orderId is not present, it shows an error toast message and navigates back to the
   *   All Order List page after a short delay
   * - Always fetches addons and master packages data
   *
   * This hook runs once on component mount.
   */
  useEffect(() => {
    const orderIdFromState = state?.orderId;

    if (orderIdFromState) {
      setOrderId(orderIdFromState);
    } else {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.ALL_ORDER_LIST);
      }, timers.TOAST_AUTO_CLOSE); //4000ms
    }
    fetchAddons();
  }, []);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  useEffect(() => {
    if (orderDetails) {
      fetchCustomerDetails(orderDetails.orderInfo.customerId);

      const deposits = orderDetails.species.reduce((total, species) => {
        const serviceDeposits = Array.isArray(species.services)
          ? species.services.reduce(
            (serviceTotal, service) =>
              serviceTotal + (service.depositAmount ?? 0),
            0,
          )
          : 0;
        return total + serviceDeposits;
      }, 0);

      setTotalDeposits(deposits);
      setTotalAmountCollected(
        deposits + (orderDetails?.orderInfo.advancePayment ?? 0),
      );

      const status = orderDetails.orderInfo.orderStatus;
      const shouldShowPickupBanner =
        status === newOrderStatuses.READY_TO_INVOICE ||
        status === newOrderStatuses.PARTIALLY_PICK_UP_READY;

      if (shouldShowPickupBanner) {
        fetchOrderInvoiceDetails(orderDetails.orderInfo.orderId);
      } else {
        setResendNotification(0);
        // setPickupBannerMessage('');
      }
    }
  }, [orderDetails]);

  const fetchOrderInvoiceDetails = async (currentOrderId: number): Promise<void> => {
    try {
      const invoicesResponse = await Axios.post(ApiEndpoints.ORDER_ALL_INVOICES, {
        id: currentOrderId,
      });

      const invoices = invoicesResponse?.data?.data ?? [];
      if (!Array.isArray(invoices) || invoices.length === 0) {
        setResendNotification(0);
        // setPickupBannerMessage('');
        return;
      }

      const latestInvoice = [...invoices].sort(
        (a, b) => Number(b.id ?? 0) - Number(a.id ?? 0),
      )[0];

      if (!latestInvoice?.id) {
        setResendNotification(0);
        // setPickupBannerMessage('');
        return;
      }

      const response = await Axios.post(ApiEndpoints.ORDER_INVOICE_VIEW, {
        id: latestInvoice.id,
      });

      const invoiceInfo = response?.data?.data?.invoiceInfo;
      setResendNotification(invoiceInfo?.resendNotification ?? 0);
      // setPickupBannerMessage(invoiceInfo?.bannerMessage ?? '');
    } catch (error) {
      setResendNotification(0);
      // setPickupBannerMessage('');
    }
  };

  const handleResendNotification = async (): Promise<void> => {
    if (!orderDetails?.orderInfo.orderId) {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      return;
    }

    try {
      await Axios.post(ApiEndpoints.RESEND_NOTIFICATION, {
        id: orderDetails.orderInfo.orderId,
      });
      showToastMessage('Notification resent successfully', 'success');
      fetchOrderDetails();
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Updates the `finishedSteps` state based on the current order status.
   *
   * This effect runs whenever `orderDetails` is defined or changes.
   * It determines which steps of the order workflow should be marked as finished
   * and updates the `finishedSteps` state accordingly.
   *
   * Workflow logic:
   * - Always starts with 'New Order'.
   * - If `orderStatus` < 7: add 'Order Processing'.
   * - If `orderStatus` === 7: add both 'Order Processing' and 'Pick up'.
   * - Otherwise: default back to just 'New Order'.
   *
   * Dependencies:
   * - Runs whenever `orderDetails` becomes defined or changes.
   */
  useEffect(() => {
    const currentOrderStatus = orderDetails?.orderInfo.orderStatus ?? 0;
    const steps = ['New Order'];

    // If the order status is less than 7 (paid), the order is still in progress
    if (currentOrderStatus < 7) {
      steps.push('Order Processing');
      // If the order status is 7 (paid), then order is paid and show pick up status
    } else if (currentOrderStatus === 7) {
      steps.push('Order Processing', 'Pick up');
    } else {
      steps.push('New Order');
    }

    setFinishedSteps(steps);
  }, [orderDetails != undefined]);

  /**
   * Fetches detailed information about a specific order using its ID.
   *
   * Makes a POST request to the `ORDER_DETAILS` API endpoint with the provided `orderId`,
   * retrieves the order data, and updates local state variables:
   * - `orderNumber` (order identifier),
   * - `orderDetails` (full order data),
   * - `estCompletionDate` (parsed estimated completion date).
   *
   * Displays a toast message if the request fails.
   *
   * @async
   * @function fetchOrderDetails
   * @returns {Promise<void>} A Promise that resolves when the data is successfully fetched and state is updated.
   * @throws Will throw an error if the API call fails.
   */
  const fetchOrderDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.ORDER_DETAILS, {
        id: orderId,
      });
      setOrderDetails(response.data.data);
      // setEstCompletionDate(
      //   dayjs(response.data.data.orderInfo.estCompletionDate),
      // );
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the list of available add-ons for product customization.
   *
   * This function sends a POST request to the `FETCH_ADDONS` API endpoint.
   * Upon successful response, it sets the retrieved add-ons into the component state
   * using `setAddons`. The expected response contains an `addOns` array.
   *
   * In case of a request failure, an error toast is displayed and the error is rethrown
   * for further handling.
   *
   * @returns {Promise<void>} A promise that resolves when the add-ons are successfully fetched and set.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Transforms a raw API order response into a structured format for local draft persistence.
   * * This function:
   * 1. Deeply maps the species, check-in details, and services.
   * 2. Filters packaging options based on the product status (Original vs. Final packing).
   * 3. Formats the data into a key-value store indexed by `customerId`.
   * 4. Persists the result to 'editOrderData' in localStorage to enable offline/refresh-safe editing.
   * * @param {OrderDetailsResponse} orderDetails - The full order object returned from the backend.
   * @returns {object} The mapped object that was saved to storage.
   */
  const mapOrderDetailsToLocalStorage = (
    orderDetails: OrderDetailsResponse,
  ) => {
    const { orderInfo, species } = orderDetails;

    const mappedSpecies = species.map((s: any) => ({
      speciesName: s.speciesTag,
      speciesId: s.speciesId, // original order species id
      butcherSpeciesId: s.butcherSpeciesId, // for master lookup
      isEdited: false,
      checkIn: {
        weight: s.checkIn?.weight ?? '',
        feet: s.checkIn?.feet ?? 0,
        inches: s.checkIn?.inches ?? 0,
        permitNumber: s.checkIn?.permitNumber ?? '',
        conformationNumber: s.checkIn?.conformationNumber ?? '',
        citesNumber: s.checkIn?.citesNumber ?? '',
        huntingLicenceId: s.checkIn?.huntingLicenceId ?? null,
        licenseNumber: s.checkIn?.licenseNumber ?? '',
        licenseExpiryDate: s.checkIn?.licenseExpiryDate ?? '',
        stateName: s.checkIn?.stateName ?? '',
        notes: s.checkIn?.notes ?? '',
      },
      services: s.services.map((svc: any) => ({
        serviceId: svc.serviceId,
        butcherServiceId: svc.butcherServiceId,
        color: svc.color ?? undefined,
        colorId: svc.colorId,
        depositAmount: svc.depositAmount ?? 0,
        status: svc.status,
      })),
      products: s.products.map((p: any) => ({
        productId: p.productId,
        butcherProductId: p.butcherProductId,
        quantity: p.quantity,
        status: p.status,
        fatPercent: p.fatPercent,
        addOnIds: p.addons.map((a: any) => a.addonId),
        packaging: p.packing
          .filter((pkg: any) =>
            p.status >= productStatuses.COMPLETE
              ? pkg.isFinalPacking === 1
              : pkg.isFinalPacking === 0,
          )
          .map((pkg: any) => ({
            productPackageTypeId: pkg.packageTypeId,
            packageType: pkg.packageType,
            size: pkg.size,
            percentage: pkg.percentage,
          })),

        packagingNotes: p.note ?? '',
      })),
      additionalNotes: s.notes ?? '',
    }));

    const mapped = {
      customerId: orderInfo.customerId,
      speciesId: species[0]?.butcherSpeciesId ?? 0,
      quantity: orderInfo.orderQuantity,
      speciesName: species[0]?.name ?? '',
      species: mappedSpecies,
    };

    // Store under new key
    localStorage.setItem(
      'editOrderData',
      JSON.stringify({ [orderInfo.customerId]: mapped }),
    );
    // Store the currentcustomizing id as 0 in the starting
    localStorage.setItem('currentCustomizingId', '0');
    return mapped;
  };

  /**
   * Toggles the visibility of an accordion section based on its index.
   *
   * If the specified index is already in the `openAccordions` state array,
   * it removes it (i.e., closes that accordion section). Otherwise, it adds
   * the index to the array (i.e., opens the section).
   *
   * @param {number} index - The index of the accordion section to toggle.
   *
   * @returns {void}
   */
  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  /**
   * Handles the download of order summary PDF
   * Uses the PdfDownloadService to download the PDF for the current order
   */
  const handlePdfDownload = async (): Promise<void> => {
    if (!orderId) {
      showToastMessage(errorMessage.ORDER_NOT_FOUND, 'error');
      return;
    }

    try {
      await PdfDownloadService.downloadOrderSummaryPdf(
        parseInt(orderId, 10),
        `order-summary-${orderDetails?.orderInfo.orderNumber || orderId}.pdf`,
        () => {
          showToastMessage(responseTexts.PDF_DOWNLOAD_SUCCESS, 'success');
        },
        (error: Error) => {
          console.error('PDF download error:', error);
          showToastMessage(errorMessage.ERROR_DOWNLOADING_PDF, 'error');
        },
      );
    } catch (error) {
      console.error('PDF download error:', error);
      showToastMessage(errorMessage.ERROR_DOWNLOADING_PDF, 'error');
    }
  };

  /**
   * Fetches detailed information for a specific customer by their unique ID.
   * * This function:
   * 1. Sends a POST request to the FETCH_CUSTOMERS endpoint with the provided ID.
   * 2. Validates that the response contains at least one customer record.
   * 3. Updates the `customer` state with the first matching result.
   * 4. Displays a toast error message if the network request fails.
   * * @async
   * @param {number} id - The unique identifier of the customer to retrieve.
   * @returns {Promise<void>} Resolves once the customer state is updated or an error is handled.
   */
  const fetchCustomerDetails = async (id: number) => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_CUSTOMERS, {
        id: id,
      });
      if (response.data.data.customers.length > 0) {
        setCustomer(response.data.data.customers[0]);
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles the edit order action by prompting user confirmation.
   *
   * - Displays a confirmation toast before proceeding with the edit action.
   * - On confirmation, triggers the `handleEdit` function.
   * - On cancel, no action is taken and the user remains on the current screen.
   *
   * Dependencies:
   * @dependency showConfirmationToast - Displays the confirmation prompt.
   * @dependency handleEdit - Executes the edit order logic upon confirmation.
   */
  const handleEditOrderClick = (): void => {
    showConfirmationToast(
      'Are you sure you want to Edit this Order?',
      'Edit Order',
      'info',
      handleEdit,
      'Edit',
      () => { }, // cancel
      'Cancel',
    );
  };

  /**
   * Generates an invoice for the current order.
   *
   * When the generation succeeds, the user can continue to the All Invoices page
   * using the same navigation payload as the ordered-items screen.
   */
  const handleGenerateInvoice = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.INVOICE_GENERATION, {
        id: orderId,
      });

      showConfirmationToast(
        response.data.message,
        'Success',
        'success',
        () => navigate(FrontendRoutes.INVOICE_LIST),
        'View All Invoices',
        () => fetchOrderDetails(),
        'Cancel',
      );
    } catch (error) {
      showToastMessage(errorMessage.ERROR_GENERATING_INVOICE, 'error');
    }
  };

  /**
   * Initiates the edit flow for an existing order.
   *
   * - Extracts and prepares customer details (if available) and triggers
   *   the new order initiation process with pre-filled data.
   * - Maps existing order details into localStorage to persist state
   *   for the editing session.
   * - Navigates the user to the order customization screen with relevant state.
   *
   * Dependencies:
   * @dependency customer - Contains customer information for pre-filling.
   * @dependency orderDetails - Holds existing order data to be edited.
   * @dependency handleNewOrderInitiation - Initializes order creation flow.
   * @dependency mapOrderDetailsToLocalStorage - Persists order data locally.
   * @dependency navigate - Handles routing to the customization screen.
   */
  const handleEdit = (): void => {
    if (customer) {
      const customerDetails = {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phoneHome: customer.phoneHome,
        primaryEmail: customer.primaryEmail,
        HuntingLicences: customer.HuntingLicences,
      };
      handleNewOrderInitiation(navigate, customerDetails);
    }
    if (orderDetails) {
      mapOrderDetailsToLocalStorage(orderDetails);
    }
    navigate('/create-order/species/customize/', {
      state: {
        from: { orderDetails: true },
        orderId: orderDetails?.orderInfo?.orderId,
      },
    });
  };

  const BreadCrumbsProps = {
    steps: AllOrderCrumbSteps,
    orderNumber: orderDetails?.orderInfo.orderNumber,
    activeItem: 'Order No.',
  };

  const showPickupBanner =
    orderDetails?.orderInfo.orderStatus === newOrderStatuses.READY_TO_INVOICE ||
    orderDetails?.orderInfo.orderStatus === newOrderStatuses.PARTIALLY_PICK_UP_READY ||
    orderDetails?.orderInfo.orderStatus === newOrderStatuses.PICK_UP_READY;

  return (
    <div className="flex h-screen w-screen">
      <SideNav active={3} />
      <div className="flex-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                Order Summary
              </h2>
              {/* Customer details badge */}
              <div className="px-4 py-1 font-semibold rounded-full border-2 w-fit text-center bg-[#DFDFD7]">
                {orderDetails?.orderInfo.customerName} | +1{' '}
                {orderDetails?.orderInfo.customerPhone}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Allow Edit order only if order status is less than or equal to 2 (partially paid) */}
              {orderDetails && orderDetails?.orderInfo.orderStatus <= 4 && (
                <button
                  onClick={handleEditOrderClick}
                  className="flex items-center gap-1 px-2 py-1 text-primary whitespace-nowrap"
                  type="button"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0 translate-y-[1px]"
                  >
                    <path
                      d="M1.55556 12.4444H2.66389L10.2667 4.84167L9.15833 3.73333L1.55556 11.3361V12.4444ZM0 14V10.6944L10.2667 0.447222C10.4222 0.30463 10.594 0.194444 10.7819 0.116667C10.9699 0.0388889 11.1676 0 11.375 0C11.5824 0 11.7833 0.0388889 11.9778 0.116667C12.1722 0.194444 12.3407 0.311111 12.4833 0.466667L13.5528 1.55556C13.7083 1.69815 13.8218 1.86667 13.8931 2.06111C13.9644 2.25556 14 2.45 14 2.64444C14 2.85185 13.9644 3.04954 13.8931 3.2375C13.8218 3.42546 13.7083 3.59722 13.5528 3.75278L3.30556 14H0ZM9.70278 4.29722L9.15833 3.73333L10.2667 4.84167L9.70278 4.29722Z"
                      fill="#004834"
                    />
                  </svg>
                  <span className="">Edit Order</span>
                </button>
              )}
              {orderDetails?.orderInfo?.invoiceable === 1 && (
                <button
                  onClick={handleGenerateInvoice}
                  type="button"
                  className="px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary whitespace-nowrap"
                >
                  Generate Invoice
                </button>
              )}
              <button
                type="button"
                onClick={handlePdfDownload}
                className="text-gray-600 hover:text-gray-900 p-2 border border-[#D4D4D4] rounded hover:border-gray-400"
                title="Download PDF"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 12L3 7L4.4 5.55L7 8.15V0H9V8.15L11.6 5.55L13 7L8 12ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
          {showPickupBanner && (
            <div className="mt-1 mb-3 rounded-md bg-[#FFF3B1] border border-[#FFD200]">
              <div className="p-1">
                <div className="flex justify-between items-center px-4 py-1">
                  <div className="flex items-center gap-x-4 min-w-0">
                    <div className="min-w-0">
                      <h2 className="font-semibold whitespace-nowrap">
                        Ready for Pickup
                      </h2>
                      {/* <div className="mt-2 text-sm text-gray-700 flex flex-wrap">
                        <span>{pickupBannerMessage}</span>
                      </div> */}
                    </div>
                  </div>
                  <button
                    onClick={handleResendNotification}
                    type="button"
                    // FIX: Enable button when no notification has been resent yet (resendNotification === 0)
                    // Disable after first resend to prevent duplicate requests
                    disabled={resendNotification > 0}
                    className="font-bold text-primary flex items-center gap-1"
                  >
                    <span>Resend Notification</span>
                  </button>
                </div>
              </div>
            </div>
          )}
          <span className="text-[#43474E] text-sm">
            Order No. {orderDetails?.orderInfo.orderNumber} Status
          </span>
          <div className="mt-1">
            <OrderProcessingBreadcrumbs
              steps={OrderProcessingStepper}
              // If the order status is less than 7 (paid), the order is still in progress
              activeStep={
                (orderDetails?.orderInfo.orderStatus ?? 0) < 7
                  ? 'Order Processing'
                  : 'Pick up'
              }
              finishedSteps={finishedSteps}
            />
          </div>

          {orderDetails?.species.map((speciesInfo, index) => (
            <div key={index}>
              <div className="border rounded-md shadow-sm mb-3 mt-4">
                <button
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
                >
                  <span>
                    {speciesInfo.speciesTag}
                    {speciesInfo.checkIn && speciesInfo.checkIn.inches && (
                      <>
                        {''} ({speciesInfo.checkIn.feet} {Length.FEET}{' '}
                        {speciesInfo.checkIn.inches} {Length.INCHES})
                      </>
                    )}
                  </span>
                  <div className="flex items-center gap-4">
                    <OrderStatusBadge
                      type="speciesStatus"
                      status={speciesInfo.status}
                    />
                    <FontAwesomeIcon
                      className="text-[#000A16]"
                      icon={
                        openAccordions.includes(index)
                          ? faChevronUp
                          : faChevronDown
                      }
                    />
                  </div>
                </button>
                {openAccordions.includes(index) && (
                  <div className="p-4">
                    {/* Checkin accordion */}
                    <div className="rounded-md overflow-hidden bg-[#EAF1F866] mt-2 border border-[#2A6FBA1A]">
                      {/* Accordion Header */}
                      <button
                        onClick={() =>
                          setCheckinOpen(checkinOpen === index ? null : index)
                        }
                        type="button"
                        className="w-full flex justify-between items-center p-2 font-semibold text-lg outline-none"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#DFEEFF]">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18ZM6 14V10.925L11.525 5.425C11.675 5.275 11.8417 5.16667 12.025 5.1C12.2083 5.03333 12.3917 5 12.575 5C12.775 5 12.9667 5.0375 13.15 5.1125C13.3333 5.1875 13.5 5.3 13.65 5.45L14.575 6.375C14.7083 6.525 14.8125 6.69167 14.8875 6.875C14.9625 7.05833 15 7.24167 15 7.425C15 7.60833 14.9667 7.79583 14.9 7.9875C14.8333 8.17917 14.725 8.35 14.575 8.5L9.075 14H6ZM7.5 12.5H8.45L11.475 9.45L11.025 8.975L10.55 8.525L7.5 11.55V12.5ZM11.025 8.975L10.55 8.525L11.475 9.45L11.025 8.975Z"
                                fill="#2A6FBA"
                              />
                            </svg>
                          </div>

                          <span>Check - In </span>
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`text-[#2A6FBA] transition-transform duration-300 ${checkinOpen === index ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>
                      {checkinOpen === index && (
                        <div className="p-3 space-y-3 bg-white">
                          {/* Info Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2">
                            {/* License Section */}
                            {speciesInfo.checkIn.licenseNumber && (
                              <div className="bg-[#F8F8F8] p-4 rounded-sm">
                                <h4 className="text-[#1C1B1F] text-lg mb-1">
                                  License:
                                </h4>

                                <div className="grid grid-cols-2 gap-y-2 text-[#1C1B1F] font-semibold">
                                  <span>
                                    {orderDetails?.orderInfo.customerName}
                                  </span>
                                  <span>{speciesInfo.checkIn.stateName}</span>
                                  <span>
                                    {speciesInfo.checkIn.licenseNumber}
                                  </span>
                                  <span>
                                    {speciesInfo.checkIn?.licenseNumber && (
                                      <span>
                                        {speciesInfo.checkIn?.licenseExpiryDate
                                          ? formatDate(
                                            speciesInfo.checkIn
                                              .licenseExpiryDate,
                                          )
                                          : '-'}
                                      </span>
                                    )}{' '}
                                    {/* {formatDate(
                                      customerDetails?.HuntingLicences?.find(
                                        (license) =>
                                          license.id ===
                                          speciesInfo.checkIn.huntingLicenceId,
                                      )?.expiryDate || '',
                                    )} */}
                                  </span>
                                </div>
                              </div>
                            )}
                            {/* Numbers Section */}
                            <div className="bg-[#F8F8F8] text-[#1C1B1F] p-4 rounded-sm space-y-3">
                              <div className="flex gap-2">
                                <span>Permit Number:</span>
                                <span className="font-semibold">
                                  {speciesInfo.checkIn?.permitNumber ?? '-'}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <span>CITES Number:</span>
                                <span className="font-semibold">
                                  {speciesInfo.checkIn?.citesNumber ?? '-'}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <span>Confirmation Number:</span>
                                <span className="font-semibold">
                                  {speciesInfo.checkIn?.conformationNumber ??
                                    '-'}
                                </span>
                              </div>
                            </div>

                            {/* Measurements Section */}
                            <div className="bg-[#F8F8F8] text-[#1C1B1F] p-4 rounded-sm space-y-3">
                              <div className="flex gap-4">
                                <span>Length:</span>
                                <span className="font-semibold">
                                  {speciesInfo.checkIn?.feet
                                    ? `${speciesInfo.checkIn.feet} Feet ${speciesInfo.checkIn.inches || 0} Inches`
                                    : '-'}
                                </span>
                              </div>
                              <div className="flex gap-4">
                                <span>Weight:</span>
                                <span className="font-semibold">
                                  {speciesInfo.checkIn?.weight || '-'}{' '}
                                  {speciesInfo.checkIn?.weight ? 'Lbs' : ''}
                                </span>
                              </div>
                            </div>

                            {/* Notes Section */}
                            <div className="bg-[#F8F8F8] text-[#1C1B1F] p-4 rounded-sm">
                              <h4 className="mb-1">Check - In Notes:</h4>
                              <p className="text-sm">
                                {speciesInfo.checkIn?.notes ?? '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Service accordion */}
                    <div className="rounded-md overflow-hidden border-2 border-[#9F57D41A] bg-white mt-2">
                      {/* Header */}
                      <button
                        onClick={() =>
                          setServiceOpen(serviceOpen === index ? null : index)
                        }
                        type="button"
                        className="w-full flex justify-between items-center p-2 mt-1 font-semibold text-lg outline-none bg-[#F5EEFB66]"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F3E8FF]">
                            {' '}
                            <svg
                              width="20"
                              height="16"
                              viewBox="0 0 20 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M0 16V6C0 5.45 0.195833 4.97917 0.5875 4.5875C0.979167 4.19583 1.45 4 2 4H5V2C5 1.45 5.19583 0.979167 5.5875 0.5875C5.97917 0.195833 6.45 0 7 0H13C13.55 0 14.0208 0.195833 14.4125 0.5875C14.8042 0.979167 15 1.45 15 2V4H18C18.55 4 19.0208 4.19583 19.4125 4.5875C19.8042 4.97917 20 5.45 20 6V16H0ZM6 bc1qdjydrajuc3xejhdg9drhkytn6zpx69zger9zr0 6V9H4V8H6V9H14V8H16V9H18V6H2ZM7 4H13V2H7V4Z"
                                fill="#9F57D4"
                              />
                            </svg>
                          </div>
                          <span>Services</span>
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`text-[#9F57D4] transition-transform duration-300 ${serviceOpen === index ? 'rotate-180' : 'rotate-0'
                            }`}
                        />
                      </button>

                      {/* Accordion Content */}
                      {serviceOpen === index && (
                        <div className="p-3">
                          {(speciesInfo.services?.length ?? 0) > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                              {/* Service Card */}
                              {speciesInfo?.services?.map((service) => {
                                const serviceStatus = service.status ?? 0;
                                const isFinalServiceStatus =
                                  serviceStatus >= serviceStatuses.COMPLETE;
                                return (
                                  <div
                                    key={service.serviceId}
                                    className={`bg-[#F8F8F8] rounded-md p-3 shadow-sm}`}
                                  >
                                    <div className="inline-block mb-1">
                                      <OrderStatusBadge
                                        type="serviceStatus"
                                        status={serviceStatus}
                                      />
                                    </div>
                                    <div
                                      className={`${isFinalServiceStatus ? 'opacity-50' : ''}`}
                                    >
                                      <div className="flex justify-between font-semibold">
                                        <span>{service?.name}</span>
                                        <div className="flex items-center gap-2">
                                          <span>${service?.price}</span>
                                        </div>
                                      </div>

                                      {service.status <
                                        OrderProcessStatus.COMPLETED && (
                                          <p className="text-sm text-gray-500 mt-1">
                                            {service.flatFeeFlag === 0
                                              ? 'Total Price to be calculated after processing'
                                              : 'Total Price is calculated as flat fee'}
                                          </p>
                                        )}

                                      {service.color && (
                                        <span className="text-[#797979] text-sm">
                                          {service.color}
                                        </span>
                                      )}

                                      {service.depositAmount != 0 && (
                                        <div className="flex justify-between items-center bg-white p-2 mt-7">
                                          <span className="text-sm font-semibold">
                                            Deposit Collected
                                          </span>
                                          <div className="gap-4 relative w-32">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                                              $ {service.depositAmount}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-[#6C7689] px-1 py-2">
                              No service added
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Product accordion */}
                    <div className="rounded-md shadow-sm mb-3 mt-4">
                      <button
                        onClick={() =>
                          setProductOpen(productOpen === index ? null : index)
                        }
                        type="button"
                        className="w-full flex justify-between items-center bg-[#FFFEFA] border border-[#AD67491A] p-2 font-semibold text-lg outline-none"
                      >
                        {/* Left Side */}
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F9E6DF]">
                            <svg
                              width="20"
                              height="16"
                              viewBox="0 0 21 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M19.0469 8.05V16C19.0469 16.55 18.8511 17.0208 18.4594 17.4125C18.0678 17.8042 17.5969 18 17.0469 18H3.04694C2.49694 18 2.02611 17.8042 1.63444 17.4125C1.24277 17.0208 1.04694 16.55 1.04694 16V8.05C0.663606 7.7 0.367772 7.25 0.159439 6.7C-0.0488946 6.15 -0.0530612 5.55 0.146939 4.9L1.19694 1.5C1.33027 1.06667 1.56777 0.708333 1.90944 0.425C2.25111 0.141667 2.64694 0 3.09694 0H16.9969C17.4469 0 17.8386 0.1375 18.1719 0.4125C18.5053 0.6875 18.7469 1.05 18.8969 1.5L19.9469 4.9C20.1469 5.55 20.1428 6.14167 19.9344 6.675C19.7261 7.20833 19.4303 7.66667 19.0469 8.05ZM12.2469 7C12.6969 7 13.0386 6.84583 13.2719 6.5375C13.5053 6.22917 13.5969 5.88333 13.5469 5.5L12.9969 2H11.0469V5.7C11.0469 6.05 11.1636 6.35417 11.3969 6.6125C11.6303 6.87083 11.9136 7 12.2469 7ZM7.74694 7C8.13027 7 8.44277 6.87083 8.68444 6.6125C8.92611 6.35417 9.04694 6.05 9.04694 5.7V2H7.09694L6.54694 5.5C6.48027 5.9 6.56777 6.25 6.80944 6.55C7.05111 6.85 7.36361 7 7.74694 7ZM3.29694 7C3.59694 7 3.85944 6.89167 4.08444 6.675C4.30944 6.45833 4.44694 6.18333 4.49694 5.85L5.04694 2H3.09694L2.09694 5.35C1.99694 5.68333 2.05111 6.04167 2.25944 6.425C2.46777 6.80833 2.81361 7 3.29694 7ZM16.7969 7C17.2803 7 17.6303 6.80833 17.8469 6.425C18.0636 6.04167 18.1136 5.68333 17.9969 5.35L16.9469 2H15.0469L15.5969 5.85C15.6469 6.18333 15.7844 6.45833 16.0094 6.675C16.2344 6.89167 16.4969 7 16.7969 7ZM3.04694 16H17.0469V8.95C16.9636 8.98333 16.9094 9 16.8844 9H16.7969C16.3469 9 15.9511 8.925 15.6094 8.775C15.2678 8.625 14.9303 8.38333 14.5969 8.05C14.2969 8.35 13.9553 8.58333 13.5719 8.75C13.1886 8.91667 12.7803 9 12.3469 9C11.8969 9 11.4761 8.91667 11.0844 8.75C10.6928 8.58333 10.3469 8.35 10.0469 8.05C9.76361 8.35 9.43444 8.58333 9.05944 8.75C8.68444 8.91667 8.28027 9 7.84694 9C7.36361 9 6.92611 8.91667 6.53444 8.75C6.14277 8.58333 5.79694 8.35 5.49694 8.05C5.14694 8.4 4.80111 8.64583 4.45944 8.7875C4.11777 8.92917 3.73027 9 3.29694 9H3.18444C3.14277 9 3.09694 8.98333 3.04694 8.95V16Z"
                                fill="#AD6749"
                              />
                            </svg>
                          </div>
                          <span>Products</span>
                        </div>
                        {/* Right Side */}
                        <div className="flex items-center gap-3">
                          {/* Arrow */}
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`text-[#AD6749] transition-transform duration-300 ${productOpen === index ? 'rotate-180' : 'rotate-0'
                              }`}
                          />
                        </div>
                      </button>

                      {productOpen === index &&
                        ((speciesInfo.products?.length ?? 0) > 0 ? (
                          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 bg-white">
                            {speciesInfo.products.map((products) => {
                              const addOnIds =
                                products.addons?.map((addon) => addon.addonId) ??
                                [];
                              const productStatus = products.status ?? 0;
                              const isFinalProductStatus =
                                productStatus >= productStatuses.COMPLETE;

                              return (
                                <div
                                  key={products?.productId}
                                  className="border border-[#0000001A] rounded-lg p-2 overflow-hidden"
                                >
                                  {/* Card Header */}
                                  <div className="bg-[#FDF5F2] p-3 border-b border-gray-100 flex flex-wrap items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <h3
                                        className={`font-semibold text-base text-[#1C1B1F] break-words whitespace-normal ${isFinalProductStatus ? 'opacity-50' : ''}`}
                                      >
                                        {products.name}
                                      </h3>
                                      {products.fatPercent !== 0 && (
                                        <p
                                          className={`text-sm text-[#6C7689] break-words whitespace-normal mt-1 ${isFinalProductStatus ? 'opacity-50' : ''}`}
                                        >
                                          {`${products.fatPercent}% Fat`}
                                        </p>
                                      )}
                                    </div>
                                    <div className="shrink-0">
                                      <OrderStatusBadge
                                        type="productStatus"
                                        status={productStatus}
                                      />
                                    </div>
                                  </div>
                                  <div
                                    className={`${isFinalProductStatus ? 'opacity-50' : ''}`}
                                  >
                                    <div className="p-2 space-y-2 text-sm">
                                      {/* Add-Ons */}
                                      {addOnIds?.length > 0 && (
                                        <div className="flex gap-2">
                                          <span className="text-[#43474E] shrink-0">
                                            Add-Ons:
                                          </span>
                                          <span className="font-semibold text-[#1C1B1F]">
                                            {addOnIds
                                              .map(
                                                (id: number) =>
                                                  addons?.find(
                                                    (addon) => addon.id === id,
                                                  )?.name ?? `Addon ${id}`,
                                              )
                                              .join(', ')}
                                          </span>
                                        </div>
                                      )}
                                      <hr className="border-gray-100" />

                                      {/* Packaging */}
                                      <div className="space-y-1 text-sm text-[#43474E]">
                                        <p className="font-semibold">
                                          Packaging:
                                        </p>
                                        {products.packing
                                          .filter((pkg) =>
                                            products.status >= productStatuses.COMPLETE
                                              ? pkg.isFinalPacking === 1
                                              : pkg.isFinalPacking === 0,
                                          )
                                          .map((packages) => (
                                            <div key={packages.packageTypeId}>
                                              <div className="flex items-center">
                                                <span>
                                                  {packages.packageType}
                                                </span>
                                                <span className="mx-2">-</span>
                                                <span>
                                                  Size:{' '}
                                                  <span className="font-bold">
                                                    {packages.size}
                                                  </span>
                                                </span>
                                                <span className="mx-2">-</span>
                                                <span>
                                                  Pkg Split:{' '}
                                                  <span className="font-bold">
                                                    {packages.percentage}%
                                                  </span>
                                                </span>
                                              </div>
                                              <hr className="border-gray-100" />
                                              {/* Notes */}
                                              {products.packagingNotes && (
                                                <div>
                                                  <p className="font-semibold text-[#1C1B1F]">
                                                    Packaging Notes:
                                                  </p>
                                                  <p className="mt-1 text-[#1C1B1F]">
                                                    {products.packagingNotes}
                                                  </p>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-[#6C7689] px-4 py-3 bg-white">
                            No products added
                          </p>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Estimated completion date */}
          <div className="bg-[#DDE8E4] rounded-xl p-4 w-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-white rounded-lg px-4 py-2 flex items-center gap-3">
                <span className="text-sm text-gray-700">
                  Advance Payment:{' '}
                  <span className="font-bold text-gray-900">
                    ${orderDetails?.orderInfo.advancePayment}
                  </span>
                </span>
                <div className="w-px h-4 bg-gray-300" />
                <span className="text-sm text-gray-700">
                  Total Deposits Made:{' '}
                  <span className="font-bold text-gray-900">
                    ${totalDeposits}
                  </span>
                </span>
                <button
                  type="button"
                  title="Sum of all the deposits made for services"
                  className="text-gray-400 hover:text-gray-600 ml-1"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="#888888"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 4.5V8.5M8 11V11.5"
                      stroke="#888888"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="bg-white rounded-lg px-4 py-2 flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  Total Amount Paid:{' '}
                  <span className="font-bold text-gray-900">
                    ${totalAmountToCollected}
                  </span>
                </span>
                <button
                  type="button"
                  title="Total of all deposits and advance payments collected for this order"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="#888888"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 4.5V8.5M8 11V11.5"
                      stroke="#888888"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Estimated completion date*/}
              <div className="text-sm text-[#888888] whitespace-nowrap">
                Estimated completion date:{' '}
                <span className="font-bold text-[#353535]">
                  {handleDateFormat(orderDetails?.orderInfo.estCompletionDate)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};